import { z } from 'zod';
/**
 * calendar#38 - the practice's CPT / HCPCS code list (Chase's CPT_CODES_MODAL_10-08_V4). One list:
 * the Calendar's chargemaster (calendar_services, Jay 2026-10-09). The EHR's Manage Settings >
 * CPT codes reads it and saves through the Calendar's /api/services with the user's own token;
 * the Calendar's Settings > CPT codes edits the same rows. RCM's claims and the progress-note CPT
 * read it too.
 */
export const CPT_CATEGORIES = [
    'Assessment',
    'Psychotherapy',
    'Crisis',
    'Family and group',
    'E/M',
    'Testing and screening',
    'Add-on',
    'HCPCS / state',
    'Other',
];
export const CPT_MODIFIERS = [
    { code: '95', label: 'Telehealth (video)' },
    { code: '93', label: 'Telehealth (audio)' },
    { code: 'GT', label: 'Telehealth (legacy)' },
    { code: 'FQ', label: 'Audio-only' },
    { code: '25', label: 'Separate E/M' },
    { code: '59', label: 'Distinct service' },
    { code: 'HO', label: "Master's" },
    { code: 'HN', label: "Bachelor's" },
    { code: 'HP', label: 'Doctoral' },
    { code: 'HQ', label: 'Group' },
    { code: 'HR', label: 'Family, client present' },
    { code: 'HS', label: 'Family, client absent' },
];
export const CPT_PLACES_OF_SERVICE = [
    { code: '02', label: 'Telehealth, not home' },
    { code: '10', label: 'Telehealth, home' },
    { code: '03', label: 'School' },
    { code: '11', label: 'Office' },
    { code: '12', label: 'Home' },
    { code: '22', label: 'Outpatient hospital' },
    { code: '53', label: 'CMHC' },
    { code: '57', label: 'Non-residential SUD' },
    { code: '99', label: 'Other' },
];
/** Five-digit CPT (90837), Category II / III (0001F, 0591T), or HCPCS Level II (H0031). */
export const CPT_CODE_RE = /^(\d{5}|\d{4}[FTU]|[A-V]\d{4})$/;
export const normalizeCptCode = (s) => String(s ?? '')
    .trim()
    .toUpperCase();
export const blankCptCode = () => ({
    id: null,
    code: '',
    shortDescription: '',
    longDescription: '',
    category: '',
    defaultFee: null,
    typicalMinutes: null,
    modifiers: [],
    placesOfService: [],
    active: true,
});
/** {} when valid, else { field: message }. `existing` is the list; the record's own id is skipped. */
export function validateCptCode(rec, existing) {
    const e = {};
    const code = normalizeCptCode(rec.code);
    if (!code)
        e.code = 'Enter a code.';
    else if (!CPT_CODE_RE.test(code))
        e.code = 'Use 5 digits (90837), 4 digits and F or T (0001F), or a letter and 4 digits (H0031).';
    else if (existing.some((c) => c.id !== rec.id && normalizeCptCode(c.code) === code))
        e.code = `${code} is already in your code list.`;
    if (!rec.shortDescription.trim())
        e.shortDescription = 'Enter a short description.';
    if (!rec.category)
        e.category = 'Pick a category.';
    if (typeof rec.defaultFee !== 'number' || Number.isNaN(rec.defaultFee))
        e.defaultFee = 'Enter a fee (0 is allowed).';
    else if (rec.defaultFee < 0)
        e.defaultFee = 'The fee cannot be negative.';
    if (rec.typicalMinutes != null &&
        (!Number.isInteger(rec.typicalMinutes) || rec.typicalMinutes < 1))
        e.typicalMinutes = 'Use a whole number of minutes, 1 or more.';
    return e;
}
export function filterCptCodes(list, query, status, category) {
    const q = query.trim().toLowerCase();
    return [...list]
        .sort((a, b) => normalizeCptCode(a.code).localeCompare(normalizeCptCode(b.code)))
        .filter((c) => {
        if (status === 'active' && !c.active)
            return false;
        if (status === 'inactive' && c.active)
            return false;
        if (category && category !== 'all' && c.category !== category)
            return false;
        if (!q)
            return true;
        return `${c.code} ${c.shortDescription} ${c.longDescription} ${c.category}`
            .toLowerCase()
            .includes(q);
    });
}
const MODIFIER_CODES = CPT_MODIFIERS.map((m) => m.code);
const POS_CODES = CPT_PLACES_OF_SERVICE.map((p) => p.code);
/** The EHR API's create / update body (the Calendar re-checks uniqueness and permission). */
export const cptCodeInputSchema = z.object({
    code: z
        .string()
        .transform(normalizeCptCode)
        .refine((c) => CPT_CODE_RE.test(c), {
        message: 'Use 5 digits (90837), 4 digits and F or T (0001F), or a letter and 4 digits (H0031).',
    }),
    shortDescription: z.string().trim().min(1).max(60),
    longDescription: z.string().max(2000).default(''),
    category: z.string().trim().min(1).max(60),
    defaultFee: z.number().min(0).max(1_000_000),
    typicalMinutes: z.number().int().min(1).max(1440).nullable(),
    modifiers: z.array(z.enum(MODIFIER_CODES)).max(MODIFIER_CODES.length),
    placesOfService: z.array(z.enum(POS_CODES)).max(POS_CODES.length),
    active: z.boolean(),
});
//# sourceMappingURL=cptCodes.js.map